import { SortCriteria, type SerpLensResponse } from '@dupe/common/types'
import { effectiveVisualScore } from '@dupe/common/helpers'

export function sortResults(
  matches: SerpLensResponse['visual_matches'],
  sortBy: SortCriteria
): SerpLensResponse['visual_matches'] {
  if (sortBy === SortCriteria.Recommended) {
    // Return original order from server (already sorted by "recommended")
    return matches
  }

  // Visual-match (DUP-326/336): admin lens that re-orders by visual
  // similarity. `effectiveVisualScore` prefers the cross-source photo
  // cosine, falls back to the Turbopuffer catalog score when photo
  // scoring was missing or zero-magnitude — so Turbopuffer matches
  // don't sink to the bottom just because the candidate embed failed
  // on them.
  if (sortBy === SortCriteria.VisualMatch) {
    const scored = [...matches].sort((a, b) => {
      const aScore = effectiveVisualScore(a)
      const bScore = effectiveVisualScore(b)
      if (aScore !== bScore) return bScore - aScore
      const aPrice = a.price?.extracted_value ?? Number.POSITIVE_INFINITY
      const bPrice = b.price?.extracted_value ?? Number.POSITIVE_INFINITY
      return aPrice - bPrice
    })
    return scored
  }

  // Consensus ranking: trust the server's order. The server's
  // `sortVisualMatches` RRF branch does more than rrfScore-desc — it also
  // applies Lens injection at reserved slots and tier tiebreakers the client
  // can't replicate from match fields alone. (As the non-furniture relevance
  // experiment's test arm, this branch also does NOT float partner picks —
  // they rank by relevance — so the client must NOT re-introduce pick-floating
  // here.) Returning `matches` as-is preserves the server order exactly; an
  // explicit client re-sort would silently undo the injection + the no-float.
  if (sortBy === SortCriteria.RrfRecommended) {
    return matches
  }

  // For price sorting, separate items with and without prices
  const withPrices: SerpLensResponse['visual_matches'] = []
  const withoutPrices: SerpLensResponse['visual_matches'] = []

  matches.forEach((match) => {
    if (match.price?.extracted_value && match.price.extracted_value > 0) {
      withPrices.push(match)
    } else {
      withoutPrices.push(match)
    }
  })

  if (sortBy === SortCriteria.Price) {
    // Sort with prices from low to high, then append without prices
    withPrices.sort((a, b) => {
      const aPrice = a.price?.extracted_value ?? 0
      const bPrice = b.price?.extracted_value ?? 0
      return aPrice - bPrice
    })
    return [...withPrices, ...withoutPrices]
  }

  if (sortBy === SortCriteria.Match) {
    // Sort with prices from high to low, then append without prices
    withPrices.sort((a, b) => {
      const aPrice = a.price?.extracted_value ?? 0
      const bPrice = b.price?.extracted_value ?? 0
      return bPrice - aPrice
    })
    return [...withPrices, ...withoutPrices]
  }

  // Default fallback to original order
  return matches
}
